import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import { ArrowLeft, Plus, Printer, Send, Trash2, X } from "lucide-react";
import {
  getAppointment, getPatientAppointments, updatePatient,
  getPrescriptionByAppointment, savePrescriptionByAppointment, sendPrescription,
  getCatalogTerms, quickAddTerm, getCatalogMedicines,
} from "../../api/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import {
  CLINICAL_TERM_TYPE, DOSAGE_QUICK_PICKS, DOSAGE_TIMING, DOSAGE_TIMING_LABEL,
  DURATION_UNIT, DURATION_UNIT_LABEL, calculateAge,
} from "../../lib/prescription-constants";
import { cn } from "../../lib/utils";

const emptyForm = () => ({
  symptoms: [], diagnoses: [], medicines: [],
  advice: "", follow_up_days: "", internal_notes: "",
});

// Shared search-and-add box for Symptoms/Diagnosis — same interaction, only
// the `type` and result list differ. Search misses fall through to
// "add as new", which is what lets the clinical vocabulary grow from real
// use instead of requiring an admin trip to the Master screen first.
function TermPicker({ type, label, selected, onAdd }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      getCatalogTerms({ type, search: query.trim(), limit: 12 })
        .then((res) => setResults(res.data || []))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, type]);

  const exactMatch = results.some((r) => r.name.toLowerCase() === query.trim().toLowerCase());
  const alreadyAdded = (name) => selected.some((s) => s.name.toLowerCase() === name.toLowerCase());

  const add = (term) => {
    if (alreadyAdded(term.name)) {
      toast.info(`${term.name} is already added.`);
      return;
    }
    onAdd({ term_id: term._id, name: term.name });
    setQuery("");
    setResults([]);
  };

  const quickAdd = async () => {
    setBusy(true);
    try {
      const res = await quickAddTerm({ name: query.trim(), type });
      add(res.data);
      toast.success(`"${res.data.name}" added to the ${label.toLowerCase()} list.`);
    } catch (err) {
      toast.error(err?.message || "Could not add this term.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search or add a ${label.toLowerCase()}…`}
          className="h-9"
        />
        {query.trim().length > 0 && (
          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-card shadow-md">
            {results.map((r) => (
              <button
                key={r._id}
                type="button"
                onClick={() => add(r)}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
                disabled={alreadyAdded(r.name)}
              >
                {r.name}
              </button>
            ))}
            {!exactMatch && (
              <button
                type="button"
                onClick={quickAdd}
                disabled={busy}
                className="flex w-full items-center gap-1.5 border-t border-border px-3 py-2 text-left text-sm text-primary hover:bg-primary-soft"
              >
                <Plus className="h-3.5 w-3.5" /> Add "{query.trim()}" as a new {label.toLowerCase()}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {selected.length === 0 && <p className="text-xs text-muted-foreground">None added yet.</p>}
        {selected.map((s, i) => (
          <Badge key={`${s.name}-${i}`} variant="secondary" className="gap-1 py-1 pl-2.5 pr-1.5">
            {s.name}
            <button
              type="button"
              aria-label={`Remove ${s.name}`}
              onClick={() => onAdd(null, i)}
              className="rounded-full p-0.5 hover:bg-black/10"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

function MedicinePicker({ cart, onAdd }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      getCatalogMedicines({ search: query.trim(), limit: 12 })
        .then((res) => setResults(res.data || []))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const alreadyAdded = (id) => cart.some((m) => m.medicine_id === id);

  const add = (medicine) => {
    if (alreadyAdded(medicine._id)) {
      toast.info(`${medicine.name} is already on this prescription.`);
      return;
    }
    onAdd({
      medicine_id: medicine._id,
      name: medicine.name,
      form: medicine.form,
      strength: medicine.strength,
      dosage: medicine.default_dosage || "",
      duration: 5,
      duration_unit: DURATION_UNIT.DAYS,
      timing: DOSAGE_TIMING.AFTER_FOOD,
      instructions: medicine.default_instructions || "",
    });
    setQuery("");
    setResults([]);
  };

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search medicines by name or generic name…"
        className="h-9"
      />
      {query.trim().length > 0 && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-card shadow-md">
          {results.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">No medicines match — add it to the Medicines master first.</p>
          )}
          {results.map((m) => (
            <button
              key={m._id}
              type="button"
              onClick={() => add(m)}
              disabled={alreadyAdded(m._id)}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-50"
            >
              <span>{m.name}</span>
              <span className="text-xs text-muted-foreground">{m.category}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MedicineLine({ medicine, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...medicine, [k]: v });

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium leading-tight">{medicine.name}</p>
          {(medicine.form || medicine.strength) && (
            <p className="text-xs text-muted-foreground">
              {medicine.form}{medicine.strength ? ` · ${medicine.strength}` : ""}
            </p>
          )}
        </div>
        <button type="button" aria-label={`Remove ${medicine.name}`} onClick={onRemove}
          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
        <div className="space-y-1.5">
          <Label className="text-xs">Dosage</Label>
          <div className="flex flex-wrap gap-1">
            {DOSAGE_QUICK_PICKS.map((p) => (
              <button key={p} type="button" onClick={() => set("dosage", p)}
                className={cn(
                  "rounded-md border px-2 py-1 text-xs tabular-nums",
                  medicine.dosage === p ? "border-primary bg-primary-soft text-primary" : "border-input hover:bg-muted"
                )}>
                {p}
              </button>
            ))}
          </div>
          <Input value={medicine.dosage} onChange={(e) => set("dosage", e.target.value)}
            placeholder="Custom, e.g. 1 tsp twice daily" className="h-8 text-sm" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Duration</Label>
          <div className="flex gap-1">
            <Input type="number" min={0} value={medicine.duration}
              onChange={(e) => set("duration", e.target.value === "" ? "" : Number(e.target.value))}
              className="h-8 w-16 text-sm" />
            <select value={medicine.duration_unit} onChange={(e) => set("duration_unit", e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-1.5 text-sm">
              {Object.values(DURATION_UNIT).map((u) => <option key={u} value={u}>{DURATION_UNIT_LABEL[u]}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Timing</Label>
          <select value={medicine.timing} onChange={(e) => set("timing", e.target.value)}
            className="h-8 w-full rounded-md border border-input bg-background px-1.5 text-sm">
            {Object.values(DOSAGE_TIMING).map((t) => <option key={t} value={t}>{DOSAGE_TIMING_LABEL[t]}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-2 space-y-1.5">
        <Label className="text-xs">Instructions (optional)</Label>
        <Input value={medicine.instructions} onChange={(e) => set("instructions", e.target.value)}
          placeholder="e.g. taper over 2 weeks" className="h-8 text-sm" />
      </div>
    </div>
  );
}

function RxPreview({ appointment, form, clinic }) {
  const patient = appointment?.patient_id;
  const doctor = appointment?.doctor_id;
  const age = calculateAge(patient?.dob);
  const addressLine = [clinic.address?.line, clinic.address?.city, clinic.address?.state, clinic.address?.pincode]
    .filter(Boolean)
    .join(", ");

  return (
    <div id="rx-print-area" className="rounded-lg border border-border bg-card p-6 text-sm">
      <div className="flex items-start justify-between border-b border-border pb-3">
        <div>
          <p className="font-serif text-lg font-semibold">{clinic.name}</p>
          {clinic.tagline && <p className="text-xs text-muted-foreground">{clinic.tagline}</p>}
          {addressLine && <p className="text-xs text-muted-foreground">{addressLine}</p>}
          {(clinic.phone || clinic.registration_no) && (
            <p className="text-xs text-muted-foreground">
              {[clinic.phone, clinic.registration_no && `Reg. ${clinic.registration_no}`].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <p>{moment().format("D MMM YYYY")}</p>
          {appointment && <p>Visit: {moment(appointment.iso_date).format("D MMM YYYY")} · {appointment.slot_time}</p>}
        </div>
      </div>

      <div className="mt-3 flex items-start justify-between gap-4 border-b border-border pb-3 text-xs">
        <div>
          <p className="font-medium text-foreground">{patient?.name}</p>
          <p className="text-muted-foreground">
            {[age != null ? `${age} yrs` : null, patient?.gender, patient?.blood_group].filter(Boolean).join(" · ") || "—"}
          </p>
          <p className="text-muted-foreground">{patient?.phone}</p>
        </div>
        <div className="text-right">
          <p className="font-medium text-foreground">{doctor?.name}</p>
          <p className="text-muted-foreground">{doctor?.specialty}</p>
          <p className="text-muted-foreground">{doctor?.reg_no}</p>
        </div>
      </div>

      {(form.symptoms.length > 0 || form.diagnoses.length > 0) && (
        <div className="mt-3 space-y-1 border-b border-border pb-3">
          {form.symptoms.length > 0 && (
            <p><span className="text-muted-foreground">Symptoms: </span>{form.symptoms.map((s) => s.name).join(", ")}</p>
          )}
          {form.diagnoses.length > 0 && (
            <p><span className="text-muted-foreground">Diagnosis: </span>{form.diagnoses.map((d) => d.name).join(", ")}</p>
          )}
        </div>
      )}

      <div className="mt-3">
        <p className="mb-1.5 font-serif text-base">℞</p>
        {form.medicines.length === 0 ? (
          <p className="text-muted-foreground">No medicines added yet.</p>
        ) : (
          <ol className="space-y-2">
            {form.medicines.map((m, i) => (
              <li key={i} className="flex gap-2">
                <span className="tabular-nums text-muted-foreground">{i + 1}.</span>
                <div>
                  <p className="font-medium text-foreground">
                    {m.name}{m.strength ? ` (${m.strength})` : ""}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {[m.dosage, m.duration ? `${m.duration} ${DURATION_UNIT_LABEL[m.duration_unit]}` : null, DOSAGE_TIMING_LABEL[m.timing], m.instructions]
                      .filter(Boolean).join(" · ")}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {(form.advice || form.follow_up_days) && (
        <div className="mt-3 border-t border-border pt-3 text-xs">
          {form.advice && <p className="mb-1">{form.advice}</p>}
          {form.follow_up_days ? <p className="text-muted-foreground">Follow up after {form.follow_up_days} day(s).</p> : null}
        </div>
      )}

      <div className="mt-8 flex justify-end">
        <div className="text-center text-xs text-muted-foreground">
          <div className="mb-8 h-px w-40 bg-border" />
          <p>{doctor?.name}</p>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_CLINIC = { name: "MediPro Clinic", tagline: "Multi-specialty clinic" };

export default function WriteRx() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const clinic = useSelector((s) => s.settings.data?.clinic) || DEFAULT_CLINIC;

  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState(null);
  const [lastVisit, setLastVisit] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [newAllergy, setNewAllergy] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getAppointment(appointmentId), getPrescriptionByAppointment(appointmentId)])
      .then(([apptRes, rxRes]) => {
        if (cancelled) return;
        setAppointment(apptRes.data);
        if (rxRes.data) {
          setForm({
            symptoms: rxRes.data.symptoms || [],
            diagnoses: rxRes.data.diagnoses || [],
            medicines: rxRes.data.medicines || [],
            advice: rxRes.data.advice || "",
            follow_up_days: rxRes.data.follow_up_days ?? "",
            internal_notes: rxRes.data.internal_notes || "",
          });
          setStatus(rxRes.data.status);
        }
        return getPatientAppointments(apptRes.data.patient_id._id, { limit: 5 });
      })
      .then((historyRes) => {
        if (cancelled || !historyRes) return;
        const previous = historyRes.data.find((a) => a._id !== appointmentId);
        setLastVisit(previous ? previous.iso_date : null);
      })
      .catch((err) => toast.error(err?.message || "Could not load this appointment."))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [appointmentId]);

  const patient = appointment?.patient_id;
  const age = useMemo(() => calculateAge(patient?.dob), [patient?.dob]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleTerm = (type) => (item, removeIndex) => {
    const key = type === CLINICAL_TERM_TYPE.SYMPTOM ? "symptoms" : "diagnoses";
    setForm((f) => {
      if (item) return { ...f, [key]: [...f[key], item] };
      return { ...f, [key]: f[key].filter((_, i) => i !== removeIndex) };
    });
  };

  const addMedicine = (medicine) => setField("medicines", [...form.medicines, medicine]);
  const updateMedicine = (index, next) =>
    setField("medicines", form.medicines.map((m, i) => (i === index ? next : m)));
  const removeMedicine = (index) => setField("medicines", form.medicines.filter((_, i) => i !== index));

  const addAllergy = async () => {
    const value = newAllergy.trim();
    if (!value || !patient) return;
    const next = [...(patient.allergies || []), value];
    try {
      await updatePatient(patient._id, { allergies: next });
      setAppointment((a) => ({ ...a, patient_id: { ...a.patient_id, allergies: next } }));
      setNewAllergy("");
    } catch (err) {
      toast.error(err?.message || "Could not save the allergy.");
    }
  };

  const payload = () => ({
    symptoms: form.symptoms,
    diagnoses: form.diagnoses,
    medicines: form.medicines,
    advice: form.advice,
    follow_up_days: form.follow_up_days === "" ? null : Number(form.follow_up_days),
    internal_notes: form.internal_notes,
  });

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      const res = await savePrescriptionByAppointment(appointmentId, { ...payload(), status: nextStatus });
      setStatus(res.data.status);
      toast.success(nextStatus === "completed" ? "Prescription completed." : "Draft saved.");
    } catch (err) {
      toast.error(err?.message || "Could not save the prescription.");
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      await sendPrescription(appointmentId);
      toast.success("Logged to Messages — WhatsApp isn't connected to a real provider yet.");
    } catch (err) {
      toast.error(err?.message || "Save the prescription before sending it.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!appointment) return null;

  return (
    <div className="space-y-4">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #rx-print-area, #rx-print-area * { visibility: visible; }
          #rx-print-area { position: absolute; inset: 0; border: none; box-shadow: none; }
        }
      `}</style>

      <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/appointments")} aria-label="Back to appointments">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="font-serif text-2xl font-semibold">Prescription</h2>
            <p className="text-sm text-muted-foreground">
              {appointment.patient_id.name} with {appointment.doctor_id.name}
            </p>
          </div>
        </div>
        <Badge variant={status === "completed" ? "secondary" : "outline"}>
          {status === "completed" ? "Completed" : "Draft"}
        </Badge>
      </div>

      {/* Patient snapshot strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border bg-card p-4 text-sm print:hidden">
        <div>
          <p className="font-medium">{patient.name}</p>
          <p className="text-xs text-muted-foreground">
            {[age != null ? `${age} yrs` : null, patient.gender, patient.blood_group].filter(Boolean).join(" · ") || "No demographics on file"}
          </p>
        </div>
        <div className="text-xs text-muted-foreground">
          <p>{patient.phone}</p>
          <p>{lastVisit ? `Last visit ${moment(lastVisit).format("D MMM YYYY")}` : "First recorded visit"}</p>
        </div>
        <div className="flex flex-1 flex-wrap items-center gap-1.5">
          {(patient.allergies || []).length === 0 && <span className="text-xs text-muted-foreground">No known allergies</span>}
          {(patient.allergies || []).map((a) => (
            <Badge key={a} variant="destructive" className="font-normal">{a}</Badge>
          ))}
          <Input
            value={newAllergy}
            onChange={(e) => setNewAllergy(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAllergy())}
            placeholder="+ Add allergy"
            className="h-7 w-32 text-xs"
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2 print:block">
        <div className="space-y-4 print:hidden">
          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold">Symptoms</h3>
            <TermPicker type={CLINICAL_TERM_TYPE.SYMPTOM} label="Symptom" selected={form.symptoms} onAdd={toggleTerm(CLINICAL_TERM_TYPE.SYMPTOM)} />
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold">Diagnosis</h3>
            <TermPicker type={CLINICAL_TERM_TYPE.DIAGNOSIS} label="Diagnosis" selected={form.diagnoses} onAdd={toggleTerm(CLINICAL_TERM_TYPE.DIAGNOSIS)} />
          </div>

          <div className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold">Medicines</h3>
            <MedicinePicker cart={form.medicines} onAdd={addMedicine} />
            {form.medicines.length > 0 && (
              <div className="mt-3 space-y-3">
                {form.medicines.map((m, i) => (
                  <MedicineLine key={i} medicine={m} onChange={(next) => updateMedicine(i, next)} onRemove={() => removeMedicine(i)} />
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-border bg-card p-4 space-y-3">
            <h3 className="text-sm font-semibold">Advice & follow-up</h3>
            <div className="space-y-1.5">
              <Label className="text-xs">Advice</Label>
              <textarea
                className="min-h-16 w-full rounded-md border border-input bg-background p-2 text-sm"
                value={form.advice}
                onChange={(e) => setField("advice", e.target.value)}
                placeholder="Rest, drink fluids, avoid spicy food…"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Follow-up after</Label>
              <Input type="number" min={0} value={form.follow_up_days}
                onChange={(e) => setField("follow_up_days", e.target.value)}
                className="h-8 w-20 text-sm" />
              <span className="text-xs text-muted-foreground">days</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Internal notes (not printed)</Label>
              <textarea
                className="min-h-12 w-full rounded-md border border-input bg-background p-2 text-sm"
                value={form.internal_notes}
                onChange={(e) => setField("internal_notes", e.target.value)}
                placeholder="Internal notes for the care desk…"
              />
            </div>
          </div>
        </div>

        <div>
          <RxPreview appointment={appointment} form={form} clinic={clinic} />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 print:hidden">
        <Button variant="outline" className="bg-card" onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Download / Print
        </Button>
        <Button variant="outline" className="bg-card" onClick={send} disabled={sending}>
          <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send"}
        </Button>
        <Button variant="outline" className="bg-card" onClick={() => save("draft")} disabled={saving}>
          {saving ? "Saving…" : "Save as Draft"}
        </Button>
        <Button onClick={() => save("completed")} disabled={saving}>
          {saving ? "Saving…" : "Save & Complete"}
        </Button>
      </div>
    </div>
  );
}
